import {Card, Stack, Typography, Box} from '@mui/material';
import moment, {Moment} from 'moment';
import {useMemo} from 'react';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryScatter,
  VictoryTooltip,
} from 'victory';

export type DateChartDomainProp = {y?: [number, number]; x?: [number, number]};
export type DateChartValuesProp = DateChartValue[][];
export type DateChartProps = {
  title?: string;
  subTitle?: [string, string?];
  values?: DateChartValuesProp;
  domain?: DateChartDomainProp;
};

type DateChartValue = {
  x: Moment;
  y: number;
  label?: string | string[];
};

/**
 * @example
 * ```tsx
  <DateChart 
    title="My Chart title"
    values={[
      [
        {x: moment().subtract(2, 'day'), y: 100},
        {x: moment().subtract(1, 'day'), y: 150},
        {x: moment(), y: 180},
      ],
    ]}
  />
* ```
* ---
* @example
* ```tsx
  <DateChart 
    title="My Chart title"
    subTitle={['subtitle 1', 'subtitle 2 (optional)']}
    values={[
      // one graph
      [
        {x: moment().subtract(2, 'day'), y: 120},
        {x: moment().subtract(1, 'day'), y: 150, label: 'Label 1 (optional)'},
        {x: moment(), y: 160, label: ['Label 1', 'Label 2', 'Label 3']},
      ],
      // another graph (optional)
      [
        {x: moment().subtract(2, 'day'), y: 100},
        {x: moment().subtract(1, 'day'), y: 125},
        {x: moment(), y: 115},
      ],
    ]}
    // axis min and max values (optional)
    domain={{
      y: [90, 190],
      // x: [..],
    }}
  />
* ```
*/
export function DateChart(props: DateChartProps) {
  const xAxisValues = useMemo(() => {
    // ---------------------------------------------------------------
    // This ensures a better distribution of the x-axis values(dates)
    // ---------------------------------------------------------------

    // Safety check
    if (!props.values?.[0]) return undefined;

    // Get begin and end dates
    const xxValues = props.values[0].map((v) => v.x);
    const {begin, end} = xxValues.reduce(
      (acc, curr) => {
        if (curr.isBefore(acc.begin)) acc.begin = curr;
        if (curr.isAfter(acc.end)) acc.end = curr;
        return acc;
      },
      {begin: xxValues.at(0)!, end: xxValues.at(-1)!}
    );

    // Generate in-between dates
    const diff = end.diff(begin, 'days');
    const delta = diff / 4;
    const inBetween = [];
    for (let i = 0; i < diff; i += delta) {
      const date = begin.clone().add(i, 'days');
      inBetween.push(date);
    }

    // Return all the dates
    return [begin, ...inBetween, end];
  }, [props.values]);

  return (
    <Card
      sx={{
        maxWidth: 520,
        position: 'relative',
      }}
    >
      {/* -------- TEXTS -------- */}
      <Stack
        sx={{
          position: 'absolute',
          width: '100%',
          placeItems: 'center',
          padding: '0 5px',
          marginTop: '8px',
        }}
      >
        {/* -------- TITLE -------- */}
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: ['18px', '22px'],
            textWrap: 'nowrap',
            color: 'gray.main',
          }}
        >
          {props.title}
        </Typography>

        {/* -------- SUB TITLE -------- */}
        <Stack flexDirection="row" gap={['14px', '32px']}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: ['12px', '14px'],
              color: '#848484',
            }}
          >
            {props.subTitle?.[0]}
          </Typography>

          {props.subTitle?.[1] && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: ['12px', '14px'],
                color: '#848484',
              }}
            >
              {props.subTitle[1]}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Box sx={{marginTop: '30px'}}>
        <VictoryChart
          standalone
          animate={{
            duration: 600,
            onLoad: {duration: 400},
          }}
          domainPadding={{x: 12, y: 5}}
          domain={props.domain}
        >
          {/* ----------------- YY AXIS ----------------- */}
          <VictoryAxis dependentAxis />

          {/* ----------------- XX AXIS ----------------- */}
          <VictoryAxis
            tickValues={xAxisValues}
            tickFormat={(tick, idx, ticks) => {
              // formats and translates the x-axis values
              return moment(tick).format('DD MMM');
            }}
          />

          {/* ----------------- CHART 1 ----------------- */}
          <VictoryArea
            style={{
              data: {
                fill: '#B4DAFC',
                stroke: '#00A3FF',
                strokeWidth: 2,
              },
            }}
            data={props.values?.[0]}
            labelComponent={<></>}
          />
          <VictoryScatter
            data={props.values?.[0]}
            size={5}
            labels={({datum}) => `${datum.y}`}
            labelComponent={
              <VictoryTooltip
                pointerLength={10}
                dy={-8}
                labelComponent={
                  <VictoryLabel
                    style={[
                      {fill: 'black', fontSize: 16},
                      {fill: 'gray', fontSize: 13},
                      {fill: 'gray', fontSize: 12},
                    ]}
                  />
                }
              />
            }
            style={{
              data: {
                fill: '#00A3FF',
              },
            }}
          />

          {/* ----------------- CHART 2 ----------------- */}
          {props.values?.[1] && (
            <VictoryArea
              style={{
                data: {
                  fill: '#ce9df2',
                  stroke: '#8F00FF',
                  strokeWidth: 2,
                },
              }}
              data={props.values?.[1]}
              labelComponent={<></>}
            />
          )}
          {props.values?.[1] && (
            <VictoryScatter
              data={props.values?.[1]}
              size={5}
              labels={({datum}) => `${datum.y}`}
              labelComponent={
                <VictoryTooltip
                  pointerLength={10}
                  dy={-8}
                  labelComponent={
                    <VictoryLabel
                      style={[
                        {fill: 'black', fontSize: 16},
                        {fill: 'gray', fontSize: 13},
                        {fill: 'gray', fontSize: 12},
                      ]}
                    />
                  }
                />
              }
              style={{
                data: {
                  fill: '#8F00FF',
                },
              }}
            />
          )}
        </VictoryChart>
      </Box>
    </Card>
  );
}
